import { Component, OnInit, OnDestroy, ViewChild, TemplateRef } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { UrlSignature } from '../../../../common/apiEndpoint';
import { SuperloginService } from '../../../../superlogin/superlogin.service';

interface AdminUser {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  otherNames?: string;
  adminType: string;
  active: boolean;
  employeeStatus: string;
  lastActivity?: string;
  lastLoginIp?: string;
  lastAccessedModule?: string;
}

interface RoleDefinition {
  key: string;
  label: string;
  description: string;
  userCount: number;
  activeCount: number;
  users: AdminUser[];
  permissions: string[];
}

@Component({
  selector: 'app-rolemanagement',
  templateUrl: './rolemanagement.component.html',
  styleUrls: ['./rolemanagement.component.scss']
})
export class RolemanagementComponent implements OnInit, OnDestroy {

  @ViewChild('actionDialogTpl') actionDialogTpl!: TemplateRef<any>;

  private destroy$ = new Subject<void>();

  // Stats
  totalRoles    = 0;
  activeRoles   = 0;
  totalUsers    = 0;
  assignedUsers = 0;

  // Data
  roles: RoleDefinition[] = [];
  allUsers: AdminUser[]   = [];
  selectedRole: RoleDefinition | null = null;

  // UI state
  isLoading        = false;
  roleUsersLoading = false;
  errorMsg         = '';
  searchTerm       = '';
  filterStatus     = 'All';

  // Current logged-in username — used as actor in all API calls
  currentUsername = '';

  // Dialog state
  dialogRef: MatDialogRef<any> | null = null;
  dialogMode: 'activate' | 'deactivate' | 'terminate' | null = null;
  selectedUser: AdminUser | null = null;
  actionForm!: FormGroup;
  actionInProgress = false;

  private readonly ROLE_DEFINITIONS: Omit<RoleDefinition, 'userCount' | 'activeCount' | 'users'>[] = [
    {
      key: 'SYSADMIN', label: 'System Administrator',
      description: 'Full system access — manages users, roles, configuration and security.',
      permissions: ['Dashboard', 'User Management', 'Role Management', 'System Configuration',
                    'Backup & Recovery', 'Audit Logs', 'Security Settings', 'All Modules']
    },
    {
      key: 'ADMIN', label: 'Administrator',
      description: 'Administrative access including user management and communications.',
      permissions: ['Dashboard', 'Student Management', 'Applicant Management', 'Communications',
                    'Facilities Management', 'User Management', 'User Sign Ups']
    },
    {
      key: 'MANAGEMENT', label: 'Management',
      description: 'Senior management with access to analytics, reports and oversight modules.',
      permissions: ['Dashboard', 'Academic Management', 'Student Analytics', 'Faculty & Staff',
                    'Financial Management', 'Reports & Analytics', 'Quality Assurance']
    },
    {
      key: 'REGISTRAR', label: 'Registrar',
      description: 'Manages student registration, academic sessions and applicant processing.',
      permissions: ['Dashboard', 'Academic Management', 'Student Management', 'Applicant Management',
                    'Courses & Programs', 'Examinations', 'Attendance & Classroom']
    },
    {
      key: 'DEAN', label: 'Dean',
      description: 'Faculty dean with access to academic and student management modules.',
      permissions: ['Dashboard', 'Academic Management', 'Student Management', 'Faculty & Staff',
                    'Courses & Programs', 'Examinations', 'Research & Publications', 'Quality Assurance']
    },
    {
      key: 'HOD', label: 'Head of Department',
      description: 'Head of department managing courses, staff and student records.',
      permissions: ['Dashboard', 'Student Management', 'Faculty & Staff', 'Courses & Programs',
                    'Examinations', 'Attendance & Classroom', 'CTVET Module']
    },
    {
      key: 'LECTURER', label: 'Lecturer',
      description: 'Teaching staff managing courses, attendance, grades and student profiles.',
      permissions: ['Dashboard', 'Courses & Programs', 'Examinations', 'Attendance & Classroom',
                    'Library Services', 'Research & Publications', 'CTVET Module']
    },
    {
      key: 'EXAM_OFFICER', label: 'Exam Officer',
      description: 'Manages examination scheduling, grade processing and transcripts.',
      permissions: ['Dashboard', 'Examinations', 'Grade Management', 'Transcripts', 'Results Processing']
    },
    {
      key: 'ACCOUNTANT', label: 'Accountant',
      description: 'Manages fees, student accounts, payroll and financial reports.',
      permissions: ['Dashboard', 'Financial Management', 'Fee Management', 'Student Accounts',
                    'Payroll Management', 'Reports & Analytics']
    },
    {
      key: 'QA', label: 'Quality Assurance',
      description: 'Oversees evaluations, accreditation, improvement plans and quality reports.',
      permissions: ['Dashboard', 'Quality Assurance', 'Course Evaluations', 'Faculty Evaluations',
                    'Accreditation', 'Communications']
    },
    {
      key: 'LIBRARIAN', label: 'Librarian',
      description: 'Manages library catalog, digital resources and book inventory.',
      permissions: ['Dashboard', 'Library Services', 'Book Management', 'Digital Resources',
                    'Library Analytics']
    },
    {
      key: 'ESTMANAGER', label: 'Estate Manager',
      description: 'Manages facilities, maintenance requests and asset tracking.',
      permissions: ['Dashboard', 'Facilities Management', 'Room Booking', 'Maintenance Requests',
                    'Asset Management', 'Transport Services']
    },
    {
      key: 'FOUNDER', label: 'Founder',
      description: 'Institutional founder with top-level dashboard visibility.',
      permissions: ['Dashboard']
    },
  ];

  constructor(
    private http: HttpClient,
    private urls: UrlSignature,
    private authService: SuperloginService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    const user = this.authService.getCurrentUser();
    this.currentUsername = user?.username || user?.email || '';
    this.actionForm = this.fb.group({
      reason: ['', [Validators.required, Validators.minLength(5)]]
    });
    this.buildRoleList();
    this.loadAllUsers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildRoleList(): void {
    this.roles = this.ROLE_DEFINITIONS.map(def => ({
      ...def, userCount: 0, activeCount: 0, users: []
    }));
    this.totalRoles = this.roles.length;
  }

  // ── GET /api/admin-users ──────────────────────────────────────────────────
  loadAllUsers(): void {
    this.isLoading = true;
    this.errorMsg  = '';
    this.http.get<AdminUser[]>(this.urls.sysaccountcreationurl)
      .pipe(takeUntil(this.destroy$), finalize(() => this.isLoading = false))
      .subscribe({
        next: (users) => {
          this.allUsers      = users;
          this.totalUsers    = users.length;
          this.assignedUsers = users.filter(u => u.active).length;
          this.computeRoleStats(users);
        },
        error: () => { this.errorMsg = 'Failed to load user data. Please retry.'; }
      });
  }

  private computeRoleStats(users: AdminUser[]): void {
    this.roles = this.roles.map(role => {
      const roleUsers  = users.filter(u => u.adminType === role.key);
      const activeOnes = roleUsers.filter(u => u.active && u.employeeStatus === 'ACTIVE');
      return { ...role, userCount: roleUsers.length, activeCount: activeOnes.length };
    });
    this.activeRoles = this.roles.filter(r => r.activeCount > 0).length;
  }

  // ── GET /api/admin-users/by-admin-type/{key} ──────────────────────────────
  selectRole(role: RoleDefinition): void {
    this.selectedRole     = role;
    this.roleUsersLoading = true;
    this.http.get<AdminUser[]>(`${this.urls.sysusersbyroleurl}${role.key}`)
      .pipe(takeUntil(this.destroy$), finalize(() => this.roleUsersLoading = false))
      .subscribe({
        next: (users) => {
          this.selectedRole = { ...role, users };
          const idx = this.roles.findIndex(r => r.key === role.key);
          if (idx > -1) this.roles[idx].users = users;
        },
        error: () => { this.selectedRole = { ...role, users: [] }; }
      });
  }

  clearSelection(): void { this.selectedRole = null; }

  // ── DIALOG OPENERS ────────────────────────────────────────────────────────

  openActivateDialog(user: AdminUser, event: Event): void {
    event.stopPropagation(); // prevent role row click
    this.selectedUser = user;
    this.dialogMode   = 'activate';
    this.actionForm.reset();
    this.dialogRef = this.dialog.open(this.actionDialogTpl, { width: '460px', disableClose: true });
  }

  openDeactivateDialog(user: AdminUser, event: Event): void {
    event.stopPropagation();
    this.selectedUser = user;
    this.dialogMode   = 'deactivate';
    this.actionForm.reset();
    this.dialogRef = this.dialog.open(this.actionDialogTpl, { width: '460px', disableClose: true });
  }

  openTerminateDialog(user: AdminUser, event: Event): void {
    event.stopPropagation();
    this.selectedUser = user;
    this.dialogMode   = 'terminate';
    this.actionForm.reset();
    this.dialogRef = this.dialog.open(this.actionDialogTpl, { width: '460px', disableClose: true });
  }

  closeDialog(): void {
    this.dialogRef?.close();
    this.selectedUser = null;
    this.dialogMode   = null;
  }

  // ── CONFIRM → calls correct backend endpoint ──────────────────────────────
  confirmAction(): void {
    if (!this.selectedUser) return;
    if (this.dialogMode !== 'activate' && this.actionForm.invalid) return;

    this.actionInProgress = true;
    const id     = this.selectedUser.id;
    const reason = this.actionForm.value.reason || '';
    const base   = this.urls.sysaccountcreationurl;

    let action$;

    if (this.dialogMode === 'activate') {
      // POST /api/admin-users/{id}/activate?activatedByUsername=
      const params = new HttpParams().set('activatedByUsername', this.currentUsername);
      action$ = this.http.post<void>(`${base}/${id}/activate`, null, { params });

    } else if (this.dialogMode === 'deactivate') {
      // POST /api/admin-users/{id}/deactivate?deactivatedByUsername=&reason=
      const params = new HttpParams()
        .set('deactivatedByUsername', this.currentUsername)
        .set('reason', reason);
      action$ = this.http.post<void>(`${base}/${id}/deactivate`, null, { params });

    } else {
      // POST /api/admin-users/{id}/terminate?terminatedByUsername=&terminationReason=
      const params = new HttpParams()
        .set('terminatedByUsername', this.currentUsername)
        .set('terminationReason', reason);
      action$ = this.http.post<void>(`${base}/${id}/terminate`, null, { params });
    }

    const currentMode = this.dialogMode; // capture before closeDialog clears it

    action$!
      .pipe(takeUntil(this.destroy$), finalize(() => this.actionInProgress = false))
      .subscribe({
        next: () => {
          this.showSnack(`User successfully ${currentMode}d`, 'success');
          const roleToRefresh = this.selectedRole;
          this.closeDialog();
          // Refresh role user list + global stats
          if (roleToRefresh) this.selectRole(roleToRefresh);
          this.loadAllUsers();
        },
        error: () => this.showSnack(`Failed to ${currentMode} user`, 'error')
      });
  }

  // ── FILTER ────────────────────────────────────────────────────────────────
  getFilteredRoles(): RoleDefinition[] {
    let filtered = this.roles;
    if (this.filterStatus === 'Has Users') filtered = filtered.filter(r => r.userCount > 0);
    if (this.filterStatus === 'No Users')  filtered = filtered.filter(r => r.userCount === 0);
    if (this.searchTerm.trim()) {
      const q = this.searchTerm.toLowerCase();
      filtered = filtered.filter(r =>
        r.label.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q) ||
        r.key.toLowerCase().includes(q)
      );
    }
    return filtered;
  }

  // ── HELPERS ───────────────────────────────────────────────────────────────
  getFullName(user: AdminUser): string {
    return `${user.firstName} ${user.lastName}`.trim() || user.username;
  }

  getUserInitials(user: AdminUser): string {
    return `${(user.firstName || '?')[0]}${(user.lastName || '?')[0]}`.toUpperCase();
  }

  getUserStatusClass(user: AdminUser): string {
    if (user.employeeStatus === 'TERMINATED') return 'terminated';
    return user.active ? 'active' : 'inactive';
  }

  getUserStatusLabel(user: AdminUser): string {
    if (user.employeeStatus === 'TERMINATED') return 'Terminated';
    return user.active ? 'Active' : 'Inactive';
  }

  getRoleDotColor(key: string): string {
    const colors: Record<string, string> = {
      SYSADMIN: '#b71c1c', ADMIN: '#c62828', MANAGEMENT: '#1565c0',
      DEAN: '#283593', HOD: '#4527a0', REGISTRAR: '#00695c',
      QA: '#f57f17', ACCOUNTANT: '#2e7d32', LECTURER: '#0277bd',
      EXAM_OFFICER: '#6a1b9a', LIBRARIAN: '#558b2f',
      ESTMANAGER: '#4e342e', FOUNDER: '#212121',
    };
    return colors[key] ?? '#555';
  }

  private showSnack(msg: string, type: 'success' | 'error'): void {
    this.snackBar.open(msg, '×', {
      duration: 4500,
      panelClass: type === 'error' ? 'snack-error' : 'snack-success',
      horizontalPosition: 'right',
      verticalPosition: 'top',
    });
  }

  get dialogTitle(): string {
    const map: Record<string, string> = {
      activate: 'Activate User Account',
      deactivate: 'Deactivate User Account',
      terminate: 'Terminate User Account'
    };
    return this.dialogMode ? map[this.dialogMode] : '';
  }

  get dialogIcon(): string {
    const map: Record<string, string> = {
      activate: 'check_circle', deactivate: 'block', terminate: 'person_off'
    };
    return this.dialogMode ? map[this.dialogMode] : '';
  }
}